import {
  useGetStoreGroupsMyCompany,
  useGetStoreMyUsers,
  useGetStoresMyCompany,
} from "@/hooks";
import {
  Notification,
  NotificationStatus,
  RoleEnum,
  SendToAllEnum,
} from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { getNotificationSchema } from "../schemas";
import { parseNotificationToFormValues } from "../utils/parseNotificationToFormValues";

export function useFormNotification(
  role: RoleEnum,
  initialData?: Notification
) {
  // Role of current user, use to build schema and parse data to submit
  // 4 role cases: company admin, head office user, store manager, store staff
  // company admin: send to all store groups & all stores (only myself)
  // head office user: send to all stores (only myself)
  // store manager: send to all members in my store
  // store staff: cannot create notification

  const getDefaultSendTime = useCallback(() => {
    const defaultTimeMs = new Date().getTime() + 60 * 60 * 1000 + 1 * 60 * 1000; // Add 1 hour and 1 minute
    return new Date(defaultTimeMs).toISOString();
  }, []);

  // Custom hook logic for creating a new notification
  const { control, handleSubmit, watch, reset, trigger, formState, getValues } =
    useForm({
      defaultValues: {
        send_time: getDefaultSendTime(),
        title: "",
        description: "",
        manager: "TFTC",
        status: NotificationStatus.DRAFT,
        send_to_all_stores: SendToAllEnum.OPTIONAL,
        send_to_all_groups: SendToAllEnum.OPTIONAL,
        send_to_all_users: SendToAllEnum.OPTIONAL,
      },
      resolver: zodResolver(getNotificationSchema(role)), // Pass role if needed
      mode: "onChange",
    });
  const timeSendNotification = watch("send_time");
  const watchStoreIds = watch("stores");
  const watchSendToAllStores = watch("send_to_all_stores");
  const watchGroupIds = watch("groups");
  const watchSendToAllGroups = watch("send_to_all_groups");
  const watchUserIds = watch("storeUsers");
  const watchSendToAllUsers = watch("send_to_all_users");

  // Watch for changes in the form state to show correct error message select receivers
  useEffect(() => {
    if (formState.isSubmitted) trigger();
  }, [
    watchStoreIds,
    watchSendToAllStores,
    watchGroupIds,
    watchSendToAllGroups,
    watchUserIds,
    watchSendToAllUsers,
  ]);

  // Set initial data if edit
  useEffect(() => {
    if (initialData) {
      const parsedValues = parseNotificationToFormValues(initialData);
      reset(parsedValues);
    }
  }, [initialData]);

  const options = useMemo(() => {
    return [
      { label: "すべて選択", value: SendToAllEnum.ALL },
      { label: "オプション", value: SendToAllEnum.OPTIONAL },
      { label: "なし", value: SendToAllEnum.NONE },
    ];
  }, []);

  // Update send time to next hour if current send time is in the past
  const updateSendTimeIfPast = () => {
    const userTime = getValues("send_time");
    const minTime = getDefaultSendTime();

    if (new Date(userTime).getTime() < new Date(minTime).getTime()) {
      reset({ ...getValues(), send_time: new Date(minTime).toISOString() });
    }
  };

  const isDisplaySelectGroups = watchSendToAllGroups === SendToAllEnum.OPTIONAL;
  const isDisplaySelectStores = watchSendToAllStores === SendToAllEnum.OPTIONAL;
  const isDisplaySelectUsers = watchSendToAllUsers === SendToAllEnum.OPTIONAL;

  // Load option data for select inputs
  // Load select data for group, store if role is companyAdmin
  // State to search option in select component
  const [searchGroups, setSearchGroups] = useState("");
  const [searchStores, setSearchStores] = useState("");
  const [searchUsers, setSearchUsers] = useState("");

  const {
    storeGroups,
    fetchNextPage: fetchNextPageGroups,
    hasNextPage: hasNextPageGroups,
    isFetching: isFetchingGroups,
  } = useGetStoreGroupsMyCompany({
    enabled: role === RoleEnum.COMPANY_ADMIN,
    search: searchGroups,
    s: searchGroups,
  });

  const {
    myCompanyStores,
    fetchNextPage: fetchNextPageStores,
    hasNextPage: hasNextPageStores,
    isFetching: isFetchingStores,
  } = useGetStoresMyCompany({
    enabled:
      role === RoleEnum.COMPANY_ADMIN || role === RoleEnum.HEAD_OFFICE_USER,
    s: searchStores,
  });

  const {
    storeUsers,
    fetchNextPage: fetchNextPageUsers,
    hasNextPage: hasNextPageUsers,
    isFetching: isFetchingUsers,
  } = useGetStoreMyUsers({
    enabled: role === RoleEnum.STORE_MANAGER,
    search: searchUsers,
    s: searchUsers,
  });

  // Create file from url if needed, return list of File for copy function
  const createFileFromUrls = async (fileItems: any[]) => {
    // {id, url, name, file}
    const files = await Promise.all(
      fileItems.map(async (item) => {
        // Return file if exists
        if (item.file) return item.file;

        // Create file from url
        const response = await fetch(item.url, {
          credentials: "include", // Include cookies for same-origin prevent CORS issues
        });
        const blob = await response.blob();
        return new File([blob], item.name || item.url, {
          type: blob.type,
        });
      })
    );
    return files;
  };

  return {
    control,
    handleSubmit,
    timeSendNotification,
    trigger,
    options,
    updateSendTimeIfPast,
    role,
    isDisplaySelectGroups,
    isDisplaySelectStores,
    isDisplaySelectUsers,

    // option data for select inputs
    setSearchGroups,
    setSearchStores,
    setSearchUsers,
    storeGroups,
    fetchNextPageGroups,
    hasNextPageGroups,
    isFetchingGroups,
    myCompanyStores,
    fetchNextPageStores,
    hasNextPageStores,
    isFetchingStores,
    storeUsers,
    fetchNextPageUsers,
    hasNextPageUsers,
    isFetchingUsers,
    createFileFromUrls,
  };
}
